import { DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { InsuranceAiRecommendationDetailRecord, InsuranceApiService } from './insurance-api.service';
import {
  AI_ENTITY_TYPE_OPTIONS,
  AI_EXPLANATION_SCALAR_FIELDS,
  AI_RECOMMENDATION_TYPE_OPTIONS,
  buildAiExplanationFactorRows,
  buildAiExplanationRuleList,
  entityTypeLabelKey
} from './insurance-snapshot-schemas';

@Component({
  standalone: true,
  selector: 'ain-insurance-ai-recommendation-detail',
  imports: [
    DecimalPipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    TextareaModule,
    TableModule,
    DialogModule,
    SelectModule,
    AinDatePipe,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page [title]="t('insurance.ai.recommendations.detailTitle')" [description]="t('insurance.ai.recommendations.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.aiRecommendations.help.detail.title'"
        [descriptionKey]="'insurance.aiRecommendations.help.detail.description'"
        [bulletKeys]="[
          'insurance.aiRecommendations.help.detail.bullets.explanation',
          'insurance.aiRecommendations.help.detail.bullets.override',
          'insurance.aiRecommendations.help.detail.bullets.accept'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (!detail()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/ai-recommendations']">{{ t('catalog.action.backToList') }}</a>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.aiRecommendations.sections.summary') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.aiRecommendations.help.summary.title'"
              [descriptionKey]="'insurance.aiRecommendations.help.summary.description'"
              [bulletKeys]="[
                'insurance.aiRecommendations.help.summary.bullets.score',
                'insurance.aiRecommendations.help.summary.bullets.status',
                'insurance.aiRecommendations.help.summary.bullets.model'
              ]" />
          </div>
          <div class="summary-grid">
            <label>
              <span>{{ t('insurance.ai.fields.recommendationType') }}</span>
              <p-select
                [options]="recommendationTypeOptions"
                optionValue="value"
                [ngModel]="detail()!.recommendation.recommendationType"
                [disabled]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.ai.fields.entityType') }}</span>
              <p-select
                [options]="entityTypeOptions"
                optionValue="value"
                [ngModel]="detail()!.recommendation.entityType"
                [disabled]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.ai.fields.entityReference') }}</span>
              <button type="button" class="link-btn" (click)="openEntity()">{{ entityReferenceLabel() }}</button>
            </label>
            <label>
              <span>{{ t('insurance.ai.fields.score') }}</span>
              <input class="readonly-input" [value]="(detail()!.recommendation.score * 100 | number:'1.0-1') + '%'" readonly />
            </label>
            <label>
              <span>{{ t('columns.status') }}</span>
              <p-select
                [options]="statusOptions"
                optionValue="value"
                [ngModel]="detail()!.recommendation.status"
                [disabled]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.ai.fields.modelVersion') }}</span>
              <input class="readonly-input" [value]="detail()!.recommendation.modelVersion" readonly />
            </label>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.ai.tabs.explanation') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.aiRecommendations.help.explanation.title'"
              [descriptionKey]="'insurance.aiRecommendations.help.explanation.description'"
              [bulletKeys]="[
                'insurance.aiRecommendations.help.explanation.bullets.summary',
                'insurance.aiRecommendations.help.explanation.bullets.json'
              ]" />
          </div>
          <p class="explanation">{{ summaryFor(detail()!.recommendation) }}</p>
          <ain-structured-json-display
            [fields]="explanationScalarFields"
            [payloadJson]="detail()!.recommendation.explanationJson"
            [excludeKeys]="['summary', 'factors', 'rules', 'entityId']"
            [emptyMessage]="t('insurance.ai.explanation.noDetails')" />
          @if (explanationFactorRows().length > 0) {
            <div class="section-gap">
              <p class="field-label">{{ t('insurance.ai.explanation.factors') }}</p>
              <p-table [value]="explanationFactorRows()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.ai.explanation.factorCode') }}</th>
                    <th>{{ t('insurance.ai.explanation.factorWeight') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.code }}</td>
                    <td>{{ row.weight }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          }
          @if (explanationRuleRows().length > 0) {
            <div class="section-gap">
              <p class="field-label">{{ t('insurance.ai.explanation.rulesTriggered') }}</p>
              <ul class="rule-list">
                @for (rule of explanationRuleRows(); track rule) {
                  <li>{{ rule }}</li>
                }
              </ul>
            </div>
          }
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.ai.tabs.override') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.aiRecommendations.help.override.title'"
              [descriptionKey]="'insurance.aiRecommendations.help.override.description'"
              [bulletKeys]="[
                'insurance.aiRecommendations.help.override.bullets.history',
                'insurance.aiRecommendations.help.override.bullets.accept',
                'insurance.aiRecommendations.help.override.bullets.reason'
              ]" />
          </div>
          @if (detail()!.overrides.length === 0) {
            <ain-state type="empty" [title]="t('insurance.aiRecommendations.states.noOverrides')" [message]="t('insurance.aiRecommendations.states.noOverridesMessage')" />
          } @else {
            <p-table [value]="detail()!.overrides">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.ai.fields.reason') }}</th>
                  <th>{{ t('insurance.uwInbox.fields.createdAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.reason }}</td>
                  <td>{{ row.overriddenAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (detail()!.recommendation.status === 'Pending') {
            <div class="actions section-gap">
              <button pButton type="button" [label]="t('insurance.ai.copilot.accept')" (click)="openAcceptDialog()"></button>
              <button pButton type="button" severity="danger" [label]="t('insurance.ai.copilot.override')" (click)="openOverrideDialog()"></button>
            </div>
          }
        </section>
      }

      <p-dialog [header]="t('insurance.ai.copilot.accept')" [modal]="true" [(visible)]="acceptDialogVisible" [style]="{ width: '480px' }">
        <p>{{ t('insurance.aiRecommendations.messages.acceptConfirm') }}</p>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="acceptDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('insurance.ai.copilot.accept')" (click)="confirmAccept()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('insurance.ai.copilot.override')" [modal]="true" [(visible)]="overrideDialogVisible" [style]="{ width: '560px' }">
        <label class="dialog-field">
          <span>{{ t('insurance.ai.copilot.overrideReason') }}</span>
          <textarea pTextarea rows="4" [(ngModel)]="overrideReason" name="overrideReason"></textarea>
        </label>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="overrideDialogVisible.set(false)"></button>
          <button pButton type="button" severity="danger" [label]="t('insurance.ai.copilot.override')" [disabled]="!overrideReason.trim()" (click)="confirmOverride()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label span { color: #475569; font-size: 12px; font-weight: 600; }
    .readonly-input { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; background: #f8fafc; color: #0f172a; }
    .explanation { margin: 0 0 12px; font-size: 14px; }
    .field-label { margin: 0 0 8px; font-weight: 600; color: #334155; }
    .section-gap { margin-top: 14px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-align: start; }
    .rule-list { margin: 0; padding-inline-start: 18px; color: #334155; }
    .dialog-field { display: grid; gap: 8px; }
    @media (max-width: 760px) {
      .summary-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceAiRecommendationDetailComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly explanationScalarFields = AI_EXPLANATION_SCALAR_FIELDS;
  readonly entityTypeOptions = AI_ENTITY_TYPE_OPTIONS;
  readonly recommendationTypeOptions = AI_RECOMMENDATION_TYPE_OPTIONS;
  readonly statusOptions = [
    { value: 'Pending', labelKey: 'insurance.aiRecommendations.status.pending' },
    { value: 'Accepted', labelKey: 'insurance.aiRecommendations.status.accepted' },
    { value: 'Overridden', labelKey: 'insurance.aiRecommendations.status.overridden' },
    { value: 'Expired', labelKey: 'insurance.aiRecommendations.status.expired' }
  ];

  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly detail = signal<InsuranceAiRecommendationDetailRecord | null>(null);
  readonly acceptDialogVisible = signal(false);
  readonly overrideDialogVisible = signal(false);

  overrideReason = '';

  readonly explanationFactorRows = computed(() =>
    buildAiExplanationFactorRows(this.detail()?.recommendation.explanationJson)
  );

  readonly explanationRuleRows = computed(() =>
    buildAiExplanationRuleList(this.detail()?.recommendation.explanationJson)
  );

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('recommendationId');
      if (id) {
        this.load(id);
      }
    });
  }

  load(id: string): void {
    this.loading.set(true);
    this.api.getAiRecommendation(id).subscribe({
      next: d => {
        this.detail.set(d);
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.detail.set(null);
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
  }

  openAcceptDialog(): void {
    this.acceptDialogVisible.set(true);
  }

  openOverrideDialog(): void {
    this.overrideDialogVisible.set(true);
  }

  confirmAccept(): void {
    this.acceptDialogVisible.set(false);
    this.accept();
  }

  confirmOverride(): void {
    if (!this.overrideReason.trim()) {
      return;
    }
    this.overrideDialogVisible.set(false);
    this.override();
  }

  accept(): void {
    const id = this.detail()?.recommendation.id;
    if (!id) return;
    this.api.acceptAiRecommendation(id).subscribe({ next: () => this.load(id) });
  }

  override(): void {
    const id = this.detail()?.recommendation.id;
    if (!id || !this.overrideReason.trim()) return;
    this.api.overrideAiRecommendation(id, { reason: this.overrideReason.trim() }).subscribe({
      next: () => {
        this.overrideReason = '';
        this.load(id);
      }
    });
  }

  entityReferenceLabel(): string {
    const recommendation = this.detail()?.recommendation;
    if (!recommendation) {
      return '-';
    }
    return `${this.t(entityTypeLabelKey(recommendation.entityType))} — ${recommendation.entityId}`;
  }

  openEntity(): void {
    const recommendation = this.detail()?.recommendation;
    if (!recommendation) {
      return;
    }

    const routes: Record<string, string> = {
      InsurancePolicy: '/insurance/policies',
      InsuranceClaim: '/insurance/claims',
      InsuranceQuote: '/insurance/quotes',
      InsuranceUwCase: '/insurance/uw-cases',
      InsuranceProposal: '/insurance/proposals'
    };
    const base = routes[recommendation.entityType];
    if (base) {
      void this.router.navigate([base, recommendation.entityId]);
    }
  }

  summaryFor(row: InsuranceAiRecommendationDetailRecord['recommendation']): string {
    try {
      const parsed = JSON.parse(row.explanationJson) as { summary?: string };
      return parsed.summary || this.t('insurance.ai.copilot.noExplanation');
    } catch {
      return this.t('insurance.ai.copilot.noExplanation');
    }
  }

  statusKey(status: InsuranceAiRecommendationDetailRecord['recommendation']['status']): string {
    const normalized = status.charAt(0).toLowerCase() + status.slice(1);
    return `insurance.aiRecommendations.status.${normalized}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
